/* DealRoomWorkstreamsPage.jsx — Canonical ChainFlow Deal Room
 * (iter-164 redesign, per the operator-supplied reference).
 *
 * The Deal Room is the *operating system for property transactions*.
 * Every section moves the deal forward — never a CRM, never a
 * dashboard, never a task manager.
 *
 * Architecture
 *   - Reuses the existing journey + operational-events backend.
 *   - Status derived from /api/v2/deals/{id}/activity (no parallel
 *     state).
 *   - "What happened?" routes through `chainStore.logOperationalEvent`
 *     → POST /api/deals/{id}/operational-event (iter-160 self-heal).
 *   - Dynamic Transaction Journey shape via `journeys.buildJourney`
 *     ({tenure, funding}).
 *   - Sidebar shell + atmosphere preserved (iter-162/163).
 */
import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, ArrowRight, Bookmark, Building2, Calendar, Check, CheckCircle2,
  ChevronDown, ChevronRight, Circle, Clock, ExternalLink, FileText, Filter,
  Flag, Globe, Hash, Home, Link2, Mail, MapPin, MessageCircle, MoreHorizontal,
  Paperclip, Phone, Plus, Scale, Send, Settings2, Share2, Sliders, Smile,
  Sparkles, Star, TrendingUp, Upload, User, Users, Zap,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { useChainStore } from "../data/chainStore";
import { useAuth } from "../data/authStore";
import { useDealActivity, invalidateDealActivity } from "../hooks/useDealActivity";
import { buildJourney, deriveJourneyStatuses, fmtJourneyDate } from "../data/journeys";
import { OperationalEventSheet } from "../components/dealroom/OperationalEventSheet";
import { SinceLastVisitStrip } from "../components/dealroom/SinceLastVisitStrip";
import { CompactBlockerRail } from "../components/dealroom/CompactBlockerRail";
import { waitToneFromDays, waitToneClass, formatWaitLabel } from "../lib/waitDuration";
import { relTimeFromIso, freshnessTone, freshnessClass } from "../lib/relativeTime";

/* ────────────────────── helpers ───────────────────────── */
const _MS_DAY = 86_400_000;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
const fmtMoney = (v) => {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(v); }
  catch { return `£${v}`; }
};
const daysBetween = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.round((t - Date.now()) / _MS_DAY);
};
const sanitizeTel = (v) => (v || "").replace(/[^+\d]/g, "");
const sanitizeWa  = (v) => (v || "").replace(/[^\d]/g, "");
const initialsOf = (s) =>
  (s || "?").split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "·";

/* ────────────────────── Top action bar ────────────────────── */
function TopActionBar({ dealName, onLogProgress, onUpdateStage, onSendChase, onShare }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <nav className="flex items-center gap-2 text-[12px] text-slate-500" data-testid="dealroom-breadcrumb">
        <Link to="/deal-rooms" className="hover:text-slate-700">Deal Rooms</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-900 font-medium truncate max-w-[18rem]">{dealName}</span>
      </nav>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onLogProgress}
          className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-full px-4 py-2 shadow-sm transition"
          data-testid="dealroom-what-happened-btn"
        >
          <Zap className="h-3.5 w-3.5" /> What happened?
        </button>
        <button type="button" onClick={onUpdateStage} className="inline-flex items-center gap-1.5 bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-sm text-slate-700 rounded-full px-3 py-2" data-testid="dealroom-update-stage-btn"><Flag className="h-3.5 w-3.5" /> Update stage</button>
        <button type="button" onClick={onSendChase} className="inline-flex items-center gap-1.5 bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-sm text-slate-700 rounded-full px-3 py-2" data-testid="dealroom-send-chase-btn"><Send className="h-3.5 w-3.5" /> Send chase</button>
        <button type="button" onClick={onShare} className="inline-flex items-center gap-1.5 bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-sm text-slate-700 rounded-full px-3 py-2" data-testid="dealroom-share-btn"><Share2 className="h-3.5 w-3.5" /> Share with client</button>
        <button type="button" className="bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 rounded-full p-2"><MoreHorizontal className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

/* ────────────────────── Property hero ────────────────────── */
function PropertyHero({ deal, property, latestEventLabel, latestEventIso }) {
  const photo = property?.imageUrl || property?.coverPhoto || null;
  const dx = daysBetween(deal?.targetExchange);
  const dc = daysBetween(deal?.targetCompletion);
  const ltvText = deal?.ltvPct ? `${deal.ltvPct}% LTV` : null;
  const depositPct = deal?.depositPct ? `${deal.depositPct}%` : null;
  const isLease = (deal?.tenure || "").toLowerCase().includes("lease");
  const hasChain = (deal?.chainStatus || "").toLowerCase().includes("chain") || deal?.chainLinks > 1;

  return (
    <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5" data-testid="dealroom-hero">
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Photo + ON TRACK pill */}
        <div className="relative lg:w-60 shrink-0 rounded-xl overflow-hidden bg-slate-100 aspect-[4/3] lg:aspect-auto lg:h-44">
          {photo
            ? <img src={photo} alt="" className="h-full w-full object-cover" />
            : <div className="h-full w-full flex items-center justify-center text-slate-300"><Home className="h-10 w-10" /></div>}
          <div className="absolute top-3 left-3 bg-slate-900/92 text-white rounded-lg px-2.5 py-1.5 backdrop-blur">
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-wide">On track</span>
            </div>
            <div className="text-[10px] text-emerald-300 mt-0.5">Healthy progress</div>
          </div>
        </div>

        {/* Identity + stats */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <h1 className="text-2xl font-bold text-slate-900 leading-tight">{deal?.name || "Untitled deal"}</h1>
            <button className="text-slate-300 hover:text-violet-500 mt-1"><Star className="h-4 w-4" /></button>
            <button className="text-slate-300 hover:text-slate-500 mt-1"><MoreHorizontal className="h-4 w-4" /></button>
          </div>
          {property?.address && <div className="text-[13px] text-slate-500 mt-0.5">{property.address}</div>}
          <div className="mt-2 flex flex-wrap gap-1.5 items-center">
            {isLease && <span className="text-[10px] uppercase font-semibold tracking-wide bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">Leasehold</span>}
            {!isLease && <span className="text-[10px] uppercase font-semibold tracking-wide bg-sky-50 text-sky-700 px-2 py-0.5 rounded">Freehold</span>}
            {hasChain && <span className="text-[10px] uppercase font-semibold tracking-wide bg-violet-50 text-violet-700 px-2 py-0.5 rounded inline-flex items-center gap-1"><Link2 className="h-3 w-3" /> Chain dependent</span>}
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Agreed price",       value: deal?.agreedPrice ? (typeof deal.agreedPrice === "string" ? deal.agreedPrice : fmtMoney(deal.agreedPrice)) : "—" },
              { label: "Mortgage",           value: deal?.mortgageAmount || "—", sub: ltvText },
              { label: "Deposit",            value: deal?.depositAmount  || "—", sub: depositPct },
              { label: "Exchange target",    value: fmtDate(deal?.targetExchange),   sub: dx != null ? `${Math.max(0, dx)} days` : null },
              { label: "Completion target",  value: fmtDate(deal?.targetCompletion), sub: dc != null ? `${Math.max(0, dc)} days` : null },
            ].map((s) => (
              <div key={s.label} className="min-w-0">
                <div className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{s.label}</div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5 truncate">{s.value}</div>
                {s.sub && <div className="text-[10px] text-slate-500 mt-0.5">{s.sub}</div>}
              </div>
            ))}
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] flex items-center gap-2" data-testid="hero-last-updated">
            {(() => {
              // iter-170 — unified momentum line on the hero. Reads
              // freshness tone from the latest activity iso, falls back
              // to a calm slate "—" pill if no movement signal yet.
              const tone = freshnessTone(latestEventIso);
              const cls  = freshnessClass(tone);
              return (
                <>
                  <span className={`h-1.5 w-1.5 rounded-full ${cls.dot}`} />
                  <span className={cls.text}>
                    Last updated: {latestEventIso ? relTimeFromIso(latestEventIso) : "—"}
                  </span>
                  {latestEventLabel && (
                    <span className="text-violet-700 font-medium ml-2">{latestEventLabel}</span>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────── Transaction Journey strip ────────────────────── */
function TransactionJourney({ deal, activities }) {
  const steps = useMemo(
    () => buildJourney({ tenure: deal?.tenure, funding: deal?.funding || (deal?.mortgageAmount ? "mortgage" : "cash") }),
    [deal],
  );
  const statuses = useMemo(() => deriveJourneyStatuses(steps, activities, deal), [steps, activities, deal]);
  const currentIdx = statuses.findIndex((s) => s.status === "in_progress");
  const completedCount = statuses.filter((s) => s.status === "completed").length;
  const stepNum = Math.min(steps.length, completedCount + 1);

  return (
    <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5" data-testid="dealroom-journey">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Transaction journey</h2>
        <span className="text-[11px] text-slate-500">Step <span className="font-semibold text-slate-700">{stepNum}</span> of {steps.length}</span>
      </div>

      <div className="relative overflow-x-auto">
        <div className="flex items-start gap-0 min-w-[920px] pb-1">
          {statuses.map((s, idx) => {
            const isLast = idx === statuses.length - 1;
            const next = statuses[idx + 1];
            const lineActive = s.status === "completed";
            const lineHalf = s.status === "in_progress";
            return (
              <div key={s.key} className="flex-1 min-w-[78px] flex flex-col items-center text-center relative">
                {/* connector */}
                {!isLast && (
                  <span
                    aria-hidden
                    className="absolute top-[15px] left-1/2 h-[2px] rounded-full"
                    style={{
                      width: "100%",
                      background: lineActive
                        ? "linear-gradient(90deg, #10B981 0%, #10B981 100%)"
                        : lineHalf
                          ? "linear-gradient(90deg, #10B981 0%, #C7D2FE 100%)"
                          : "#E2E8F0",
                    }}
                  />
                )}
                {/* node */}
                <div className="relative z-10">
                  {s.status === "completed" ? (
                    <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-sm" data-testid={`journey-step-${s.key}-completed`}>
                      <Check className="h-4 w-4" strokeWidth={3} />
                    </div>
                  ) : s.status === "in_progress" ? (
                    <div
                      className="h-8 w-8 rounded-full bg-violet-600 text-white flex items-center justify-center ring-4 ring-violet-100"
                      style={{ boxShadow: "0 0 0 6px rgba(139,92,246,0.10), 0 8px 18px -4px rgba(124,58,237,0.45)" }}
                      data-testid={`journey-step-${s.key}-current`}
                    >
                      <Hash className="h-3.5 w-3.5" />
                    </div>
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-white ring-1 ring-slate-200 text-slate-300 flex items-center justify-center" data-testid={`journey-step-${s.key}-pending`}>
                      <Circle className="h-3 w-3" />
                    </div>
                  )}
                </div>
                {/* label */}
                <div className={`mt-3 text-[11px] leading-tight whitespace-pre-line ${
                  s.status === "completed" ? "text-slate-700"
                    : s.status === "in_progress" ? "text-violet-700 font-semibold"
                    : "text-slate-400"
                }`}>
                  {s.label}
                </div>
                {/* date / status caption */}
                <div className={`mt-1 text-[10px] ${
                  s.status === "in_progress" ? "text-violet-700 font-semibold"
                    : s.status === "completed" ? "text-slate-400"
                    : "text-slate-300"
                }`}>
                  {s.status === "completed" ? fmtJourneyDate(s.completedAt || deal?.createdAt) :
                   s.status === "in_progress" ? "In progress" : "Pending"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        className="mt-5 w-full inline-flex items-center justify-center gap-2 text-[12.5px] text-slate-500 hover:text-violet-700 rounded-lg ring-1 ring-slate-200 hover:ring-violet-300 hover:bg-violet-50/40 py-2.5 transition"
        data-testid="dealroom-view-timeline-btn"
      >
        <Calendar className="h-3.5 w-3.5" /> View full timeline and history
      </button>
    </section>
  );
}

/* ────────────────────── Operational Workstreams (table style) ────────────────────── */
function _statusPill(label) {
  const map = {
    "In progress": "bg-violet-50 text-violet-700",
    "On track":    "bg-emerald-50 text-emerald-700",
    "Waiting":     "bg-amber-50 text-amber-700",
    "At risk":     "bg-rose-50 text-rose-700",
    "Completed":   "bg-slate-100 text-slate-600",
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-md px-2 py-0.5 ${map[label] || "bg-slate-100 text-slate-600"}`}>
      {label}
    </span>
  );
}

function OperationalWorkstreamsTable({ rows, onOpenAdd }) {
  return (
    <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5" data-testid="dealroom-workstreams">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Operational workstreams</h2>
        <div className="flex items-center gap-2">
          <button className="text-[11px] text-slate-600 bg-white ring-1 ring-slate-200 rounded-md px-2 py-1 inline-flex items-center gap-1">All workstreams <ChevronDown className="h-3 w-3" /></button>
          <button className="text-slate-500 hover:text-slate-700 bg-white ring-1 ring-slate-200 rounded-md p-1.5"><Hash className="h-3.5 w-3.5" /></button>
          <button className="text-slate-500 hover:text-slate-700 bg-white ring-1 ring-slate-200 rounded-md p-1.5"><Settings2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              <th className="text-left font-semibold py-2 pr-3 w-[200px]">Workstream</th>
              <th className="text-left font-semibold py-2 pr-3 w-[110px]">Status</th>
              <th className="text-left font-semibold py-2 pr-3 w-[200px]">Owner</th>
              <th className="text-left font-semibold py-2 pr-3">Waiting on</th>
              <th className="text-left font-semibold py-2 pr-3 w-[120px]">Waiting</th>
              <th className="text-left font-semibold py-2 pr-3">Next up</th>
              <th className="w-6"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40 transition" data-testid={`workstream-row-${r.id}`}>
                <td className="py-3 pr-3">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${r.toneClass}`}>
                      <r.Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{r.label}</div>
                      <div className="text-[11px] text-slate-500 truncate">{r.subtitle}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-3">{_statusPill(r.status)}</td>
                <td className="py-3 pr-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-7 w-7 rounded-full bg-slate-200 text-[10px] font-medium text-slate-700 flex items-center justify-center shrink-0">
                      {initialsOf(r.ownerName)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-medium text-slate-900 truncate">{r.ownerName || "—"}</div>
                      <div className="text-[10.5px] text-slate-500 truncate">{r.ownerRole || ""}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-3 text-[12.5px] text-slate-700">{r.waitingOn || "—"}</td>
                {(() => {
                  // Unified waiting-duration system (iter-169). Tone
                  // band derives from the canonical 0-2 / 3-5 / 6+ doctrine;
                  // "fresh" responses (Replied today) read emerald.
                  const kind = r.waitingKind || "waiting";
                  const tone = kind === "fresh" ? "fresh" : waitToneFromDays(r.waitingDays);
                  const cls  = waitToneClass(tone);
                  const label = formatWaitLabel({ days: r.waitingDays, kind });
                  return (
                    <td className={`py-3 pr-3 text-[12.5px] tabular-nums font-semibold ${cls.text}`} data-testid={`workstream-wait-${r.id}`} data-tone={tone}>
                      {r.waitingDays == null ? "—" : label}
                    </td>
                  );
                })()}
                <td className="py-3 pr-3 text-[12.5px] text-slate-700">{r.nextUp || "—"}</td>
                <td className="py-3 pr-1 text-slate-300"><ChevronDown className="h-4 w-4" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={onOpenAdd}
        className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-violet-700 hover:text-violet-900"
        data-testid="dealroom-add-workstream-btn"
      >
        <Plus className="h-3.5 w-3.5" /> Add workstream
      </button>
    </section>
  );
}

/* ────────────────────── Activity Feed ────────────────────── */
function ActivityFeed({ rows, onSubmit, busy, value, setValue }) {
  const [tab, setTab] = useState("all");
  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (tab === "all") return rows;
    return rows.filter((r) => {
      const t = (r.activity_type || "").toLowerCase();
      if (tab === "chases") return t.includes("chase");
      if (tab === "documents") return t.includes("doc") || t.includes("file");
      if (tab === "updates") return !t.includes("chase") && !t.includes("doc");
      return true;
    });
  }, [rows, tab]);

  const grouped = useMemo(() => {
    // iter-181 — group into the four canonical Phase 2 buckets so
    // the activity feed reads as an operational narrative, never a
    // flat CRM log. Today · Yesterday · Earlier this week · Earlier.
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yest  = new Date(today); yest.setDate(yest.getDate() - 1);
    // UK convention — week starts Monday 00:00.
    const dowMon = (now.getDay() + 6) % 7;
    const startOfWeek = new Date(today); startOfWeek.setDate(today.getDate() - dowMon);
    const g = { Today: [], Yesterday: [], "Earlier this week": [], Earlier: [] };
    (filtered || []).slice(0, 30).forEach((r) => {
      const ts = Date.parse(r.created_at || r.at || 0);
      if (!ts) return g.Earlier.push(r);
      const d = new Date(ts); d.setHours(0,0,0,0);
      if      (d.getTime() === today.getTime()) g.Today.push(r);
      else if (d.getTime() === yest.getTime())  g.Yesterday.push(r);
      else if (d.getTime() >= startOfWeek.getTime()) g["Earlier this week"].push(r);
      else                                      g.Earlier.push(r);
    });
    return g;
  }, [filtered]);

  /* iter-181 — milestone classifier. Major operational moments
   *  breathe more (stronger title type, calm tinted surface)
   *  while passive entries stay quiet. */
  const isMilestoneActivity = (r) => {
    const blob = `${r.title || ""} ${r.text || ""} ${r.description || ""} ${r.activity_type || ""}`.toLowerCase();
    return /(received|issued|returned|signed|exchanged|completed|cleared|approved|offer\b|memorandum|searches\sreturned|mortgage\soffer|contracts?\sreceived|exchange\sdate|deadline|risk)/i.test(blob);
  };

  const Tab = ({ id, label, Icon }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`inline-flex items-center gap-1.5 text-[12px] pb-1.5 border-b-2 transition ${
        tab === id ? "border-violet-500 text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
      data-testid={`activity-tab-${id}`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  /* iter-183 — refined activity row.
   *  Linear / Stripe / Notion calmness:
   *    LEFT  : timestamp · 11px · slate-400 · 56px column (calm)
   *    CENTRE: 7px coloured dot + soft icon · title · body
   *    RIGHT : kind pill · avatar · ellipsis
   *  Milestone rows breathe more (calm tinted surface). Passive
   *  rows stay quiet. Hover-revealed inline Chase on blocker/risk
   *  rows. No competing metadata, no compressed sub-lines, no
   *  hard dividers — soft hairline only on hover. */
  const Row = ({ r }) => {
    const t = new Date(r.created_at || r.at || Date.now());
    const time = t.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const milestone = isMilestoneActivity(r);
    const blob = `${r.title || ""} ${r.text || ""} ${r.description || ""}`.toLowerCase();
    const showChase = /chase|waiting on|outstanding|overdue|blocker|risk/i.test(blob);
    const tone = (r.activity_type || "").toLowerCase();
    const dotColor = tone.includes("legal")   ? "#7C3AED"
                   : tone.includes("lease")   ? "#10B981"
                   : tone.includes("mortgage")? "#6366F1"
                   : milestone                 ? "#10B981"
                   : "#94A3B8";
    const tagBg = tone.includes("legal")    ? "rgba(124,58,237,0.10)"
                : tone.includes("lease")    ? "rgba(16,185,129,0.10)"
                : tone.includes("mortgage") ? "rgba(99,102,241,0.10)"
                : "rgba(148,163,184,0.10)";
    const tagFg = tone.includes("legal")    ? "#6D28D9"
                : tone.includes("lease")    ? "#047857"
                : tone.includes("mortgage") ? "#4338CA"
                : "#475569";
    const wrapCls = milestone ? "py-2.5" : "py-2";
    return (
      <div className={`group relative flex items-start gap-3 px-1 ${wrapCls}`} data-testid={`activity-row-${r.id || time}`}>
        {/* LEFT — timestamp column */}
        <span className="text-[11px] tabular-nums text-slate-400 w-14 mt-[3px] shrink-0 leading-snug">{time}</span>
        {/* CENTRE — dot + body */}
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          <span
            aria-hidden
            className="mt-[7px] inline-block rounded-full shrink-0"
            style={{ width: 7, height: 7, background: dotColor, boxShadow: `0 0 0 3px ${dotColor}1a` }}
          />
          <div className="min-w-0 flex-1">
            <div className={`${milestone ? "text-[13.5px] font-semibold" : "text-[13px] font-medium"} text-slate-900 leading-snug`}>
              {r.title || r.text || "Update"}
            </div>
            {r.description && (
              <div className="text-[12px] text-slate-500 mt-0.5 leading-snug line-clamp-2">{r.description}</div>
            )}
          </div>
        </div>
        {/* RIGHT — kind tag + actor + ellipsis */}
        <div className="flex items-center gap-1.5 shrink-0 mt-[2px]">
          {r.workstream_label && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-md leading-none"
              style={{ background: tagBg, color: tagFg }}
            >
              {r.workstream_label}
            </span>
          )}
          {(r.actor_name || r.created_by_name) && (
            <span
              className="h-5 w-5 rounded-full text-[9px] font-semibold text-slate-700 flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #E2E8F0, #CBD5E1)" }}
              title={r.actor_name || r.created_by_name}
            >
              {String(r.actor_name || r.created_by_name).split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
            </span>
          )}
          {showChase && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onSubmit?.(`Chased ${r.actor_name || r.title || "counterparty"}`);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-[10.5px] font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md"
              data-testid={`activity-chase-${r.id || time}`}
            >
              <Send className="h-3 w-3" /> Chase
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5" data-testid="dealroom-activity">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Activity feed</h2>
        <button className="text-[11px] text-slate-500 hover:text-slate-700 ring-1 ring-slate-200 rounded-full px-2.5 py-1">View all</button>
      </div>

      <div className="flex items-center gap-5 mb-3 border-b border-slate-100">
        <Tab id="all"       label="All"       Icon={MessageCircle} />
        <Tab id="chases"    label="Chases"    Icon={Send} />
        <Tab id="documents" label="Documents" Icon={FileText} />
        <Tab id="updates"   label="Updates"   Icon={Sparkles} />
      </div>

      {!rows ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading activity…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center">Nothing in this view yet.</div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([label, list]) => list.length > 0 && (
            <div key={label} data-testid={`activity-bucket-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              {/* iter-181 — calm uppercase section header rhythm
               *  matching the sidebar section labels. Quiet hairline,
               *  count to the right. */}
              <header className="flex items-center gap-2 mb-1.5">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>
                <span className="h-px flex-1 bg-slate-100" />
                <span className="text-[10.5px] text-slate-400 tabular-nums">{list.length}</span>
              </header>
              <div className="group">{list.map((r) => <Row key={r.id || `${r.created_at}-${r.title}`} r={r} />)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSubmit(value.trim()); }}
        className="mt-4 flex items-center gap-2 bg-slate-50 ring-1 ring-slate-200 rounded-full px-3 py-1.5"
      >
        <div className="h-7 w-7 rounded-full bg-violet-100 text-violet-700 text-[11px] font-bold flex items-center justify-center shrink-0">RD</div>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Write an update, @mention someone or log a note…"
          className="flex-1 bg-transparent border-0 outline-none text-[13px] text-slate-700 placeholder:text-slate-400"
          data-testid="dealroom-activity-composer"
          disabled={busy}
        />
        <button type="button" className="p-1.5 text-slate-400 hover:text-slate-600"><Paperclip className="h-3.5 w-3.5" /></button>
        <button type="button" className="p-1.5 text-slate-400 hover:text-slate-600"><Smile className="h-3.5 w-3.5" /></button>
        <button
          type="submit"
          disabled={busy || !value.trim()}
          className="h-7 w-7 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white inline-flex items-center justify-center transition"
          data-testid="dealroom-activity-submit"
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </form>
    </section>
  );
}

/* ────────────────────── Right rail cards ────────────────────── */
function CardShell({ title, accent = null, children, testid }) {
  return (
    <section className="rounded-xl bg-white ring-1 ring-slate-200 p-4" data-testid={testid}>
      <header className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{title}</h3>
        {accent}
      </header>
      {children}
    </section>
  );
}

function StatusOverviewCard({ allClear = true }) {
  return (
    <CardShell
      title="Status overview"
      accent={<span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${allClear ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{allClear ? "All clear" : "Attention"}</span>}
      testid="dealroom-status-overview"
    >
      <div className="flex items-start gap-3">
        <div className="h-7 w-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 mt-0.5">
          <Check className="h-4 w-4" strokeWidth={3} />
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Transaction progressing normally</div>
          <ul className="mt-1.5 space-y-0.5 text-[11.5px] text-slate-500">
            <li>No outstanding actions</li>
            <li>All parties are up to date</li>
          </ul>
        </div>
      </div>
    </CardShell>
  );
}

function KeyDatesCard({ targetExchange, targetCompletion, activities, latestEventIso }) {
  const dx = daysBetween(targetExchange);
  const dc = daysBetween(targetCompletion);
  const Block = ({ label, days, date, pct }) => (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">{label}</div>
      <div className="flex items-baseline gap-3">
        <div className="text-3xl font-bold tabular-nums text-violet-700 leading-none">{days == null ? "—" : Math.max(0, days)}</div>
        <div>
          <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Days<br/>to {label.replace("Target ", "").toLowerCase().replace(" target", "")}</div>
        </div>
      </div>
      <div className="mt-1.5 text-sm text-slate-900 font-medium">{fmtDate(date)}</div>
      {days != null && (
        <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-violet-500 rounded-full" style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="mt-1 text-[10px] text-slate-400">{pct}% to target</div>
    </div>
  );
  const pctDx = dx == null ? 0 : Math.min(99, Math.max(0, 100 - Math.min(100, (dx / 60) * 100)));
  const pctDc = dc == null ? 0 : Math.min(99, Math.max(0, 100 - Math.min(100, (dc / 90) * 100)));
  return (
    <CardShell title="Key dates" testid="dealroom-key-dates">
      <div className="space-y-5">
        <Block label="Exchange target"   days={dx} date={targetExchange}   pct={Math.round(pctDx)} />
        <Block label="Completion target" days={dc} date={targetCompletion} pct={Math.round(pctDc)} />
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Momentum</div>
          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">On track</span>
        </div>
        {(() => {
          // iter-170 — unified momentum readout. Derives the 14-day
          // activity count + last-move timestamp from the real
          // activities array (passed from the page). Falls back to
          // calm "—" rather than a stale hardcoded "5d ago".
          const cutoff = Date.now() - 14 * 86_400_000;
          const last14 = (activities || []).filter((a) => {
            const t = Date.parse(a?.iso || a?.createdAt || a?.created_at || a?.occurredAt || a?.occurred_at || a?.timestamp || a?.ts || "");
            return !Number.isNaN(t) && t >= cutoff;
          });
          const tone = freshnessTone(latestEventIso);
          const cls  = freshnessClass(tone);
          return (
            <>
              <div className="text-[11.5px] text-slate-500" data-testid="dealroom-momentum-count">
                <span className="font-semibold text-slate-700">{last14.length} {last14.length === 1 ? "activity" : "activities"}</span> in the last 14 days
              </div>
              <div className={`text-[10.5px] ${cls.text} inline-flex items-center gap-1 mt-0.5`} data-testid="dealroom-momentum-last">
                <span className={`h-1 w-1 rounded-full ${cls.dot}`} />
                Last move {latestEventIso ? relTimeFromIso(latestEventIso) : "—"}
              </div>
            </>
          );
        })()}
        {/* tiny sparkline */}
        <svg viewBox="0 0 120 30" className="mt-2 w-full h-7" aria-hidden>
          <polyline
            fill="none" stroke="#10B981" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
            points="0,22 14,18 28,16 42,14 56,8 70,12 84,6 98,14 112,10 120,8"
          />
        </svg>
      </div>
    </CardShell>
  );
}

/* ─── Visibility / access chips (iter-183) ─────────────────────────
 *  Calm operational role chips beneath each participant — Internal /
 *  Full access / Solicitor access / Read only / Buyer access /
 *  Seller access. Subtle low-contrast tints, thin borders, small
 *  type. Operational trust signal, never a badge of attention.
 */
const VISIBILITY_TONE = {
  internal:      { label: "Internal",        bg: "rgba(16,185,129,0.08)",  fg: "#047857", border: "rgba(16,185,129,0.18)" },
  full:          { label: "Full access",     bg: "rgba(99,102,241,0.07)",  fg: "#4338CA", border: "rgba(99,102,241,0.18)" },
  solicitor:     { label: "Solicitor access",bg: "rgba(139,92,246,0.08)",  fg: "#6D28D9", border: "rgba(139,92,246,0.20)" },
  read_only:     { label: "Read only",       bg: "rgba(148,163,184,0.12)", fg: "#475569", border: "rgba(148,163,184,0.22)" },
  buyer:         { label: "Buyer access",    bg: "rgba(99,102,241,0.07)",  fg: "#4338CA", border: "rgba(99,102,241,0.18)" },
  seller:        { label: "Seller access",   bg: "rgba(245,158,11,0.08)",  fg: "#B45309", border: "rgba(245,158,11,0.20)" },
};

/* Derive a sensible default visibility from the party role label. */
function deriveVisibility(p) {
  if (p?.visibility && VISIBILITY_TONE[p.visibility]) return p.visibility;
  const role = String(p?.role || p?.roleLabel || "").toLowerCase();
  if (/agent|negotiator|director|admin|internal|me|you/i.test(role)) return "internal";
  if (/buyer.*solicitor|seller.*solicitor|conveyancer/i.test(role))   return "solicitor";
  if (/buyer/i.test(role))                                            return "buyer";
  if (/seller|vendor/i.test(role))                                    return "seller";
  if (/broker|mortgage|surveyor|search/i.test(role))                  return "read_only";
  return "full";
}

const VisibilityChip = ({ visibility, testid }) => {
  const v = VISIBILITY_TONE[visibility] || VISIBILITY_TONE.full;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-md leading-none"
      style={{ background: v.bg, color: v.fg, border: `1px solid ${v.border}` }}
    >
      {v.label}
    </span>
  );
};

function InvolvedPartiesCard({ parties, onManage }) {
  const visible = parties.slice(0, 5);
  const overflow = parties.length - visible.length;
  return (
    <CardShell
      title="Involved parties"
      accent={<button type="button" onClick={onManage} className="text-[11px] font-medium text-violet-700 hover:text-violet-900">Manage</button>}
      testid="dealroom-involved-parties"
    >
      <div className="space-y-3.5">
        {visible.map((p) => {
          const visibility = deriveVisibility(p);
          return (
          <div key={p.id} className="flex items-center gap-3" data-testid={`party-row-${p.id}`}>
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 text-[11px] font-medium text-slate-700 flex items-center justify-center shrink-0">
              {initialsOf(p.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-slate-900 truncate">{p.name || "—"}</div>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-[11px] text-slate-500 truncate">{p.roleLabel}</span>
                <VisibilityChip visibility={visibility} testid={`party-visibility-${p.id}`} />
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {(() => {
                // Unified waiting-duration system. The party rail reads the
                // operational signal coming through `waitDays` / `waitKind`
                // attached by `buildParties()`. Default → "Active" emerald
                // for participants with no explicit wait state.
                const kind = p.waitKind || (p.waitDays != null ? "waiting" : null);
                if (!kind) {
                  return (
                    <span className="text-[10px] inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid={`party-status-${p.id}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {p.statusLabel || "Active"}
                    </span>
                  );
                }
                const tone = kind === "fresh" ? "fresh" : waitToneFromDays(p.waitDays);
                const cls  = waitToneClass(tone);
                const label = formatWaitLabel({ days: p.waitDays, kind });
                return (
                  <span className={`text-[10px] inline-flex items-center gap-1 font-medium ${cls.text}`} data-testid={`party-status-${p.id}`} data-tone={tone}>
                    <span className={`h-1.5 w-1.5 rounded-full ${cls.dot}`} /> {label}
                  </span>
                );
              })()}
              {p.phone && (
                <a href={`tel:${sanitizeTel(p.phone)}`} className="text-slate-400 hover:text-slate-700 cf-inline-icon" aria-label={`Call ${p.name}`} data-testid={`party-call-${p.id}`}>
                  <Phone className="h-3.5 w-3.5" />
                </a>
              )}
              {p.email && (
                <a href={`mailto:${p.email}`} className="text-slate-400 hover:text-slate-700 cf-inline-icon" aria-label={`Email ${p.name}`} data-testid={`party-email-${p.id}`}>
                  <Mail className="h-3.5 w-3.5" />
                </a>
              )}
              {p.phone && (
                <a href={`https://wa.me/${sanitizeWa(p.phone)}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-emerald-600 cf-inline-icon" aria-label={`WhatsApp ${p.name}`} data-testid={`party-whatsapp-${p.id}`}>
                  <MessageCircle className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </div>
          );
        })}
      </div>
      {overflow > 0 && (
        <button type="button" onClick={onManage} className="mt-3 text-[12px] font-medium text-violet-700 hover:text-violet-900 inline-flex items-center gap-1">
          +{overflow} more parties <ChevronRight className="h-3 w-3" />
        </button>
      )}
    </CardShell>
  );
}

function DealHealthCard({ tone = "emerald" }) {
  const label = tone === "emerald" ? "Strong" : tone === "amber" ? "Stable" : "At risk";
  const bg = tone === "emerald" ? "bg-emerald-50 text-emerald-700"
           : tone === "amber" ? "bg-amber-50 text-amber-700"
           : "bg-rose-50 text-rose-700";
  return (
    <CardShell title="Deal health" testid="dealroom-health">
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className={`inline-flex items-center text-sm font-semibold rounded-md px-2.5 py-1 ${bg}`}>{label}</span>
          <div className="text-[11.5px] text-slate-500 mt-2">No active risks</div>
        </div>
        <svg viewBox="0 0 120 40" className="w-32 h-10" aria-hidden>
          <polyline
            fill="none" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
            points="0,28 12,25 24,22 36,24 48,16 60,22 72,8 84,18 96,14 108,20 120,16"
          />
        </svg>
      </div>
      <button type="button" className="mt-3 text-[12px] font-medium text-violet-700 hover:text-violet-900 inline-flex items-center gap-1">
        View full health report <ArrowRight className="h-3 w-3" />
      </button>
    </CardShell>
  );
}

function QuickLinksCard({ links, onCopy }) {
  const Btn = ({ label, href, Icon, onClick, testid }) => (
    <a
      href={href || "#"}
      target={href ? "_blank" : undefined}
      rel="noreferrer"
      onClick={onClick}
      className="flex items-center gap-2 text-[12px] text-slate-700 bg-white ring-1 ring-slate-200 hover:bg-slate-50 rounded-lg px-2.5 py-2 transition"
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5 text-slate-500" /> <span className="truncate">{label}</span>
    </a>
  );
  return (
    <CardShell title="Quick links" testid="dealroom-quick-links">
      <div className="grid grid-cols-3 gap-2">
        <Btn label="Rightmove"    href={links.rightmove}   Icon={ExternalLink} testid="quick-rightmove" />
        <Btn label="Zoopla"       href={links.zoopla}      Icon={ExternalLink} testid="quick-zoopla" />
        <Btn label="Agent website" href={links.agent}      Icon={Globe}        testid="quick-agent" />
        <Btn label="EPC"          href={links.epc}         Icon={FileText}     testid="quick-epc" />
        <Btn label="Copy listing link" onClick={(e) => { e.preventDefault(); onCopy(); }} Icon={Link2} testid="quick-copy" />
        <Btn label="Property log" href={links.propertyLog} Icon={Hash}         testid="quick-property-log" />
      </div>
    </CardShell>
  );
}

/* ════════════════════ Main page ════════════════════ */
export default function DealRoomWorkstreamsPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const store = useChainStore();
  const deal = useMemo(() => store.deals.find((d) => d.id === dealId), [store.deals, dealId]);
  const property = useMemo(() => (deal?.propertyId ? store.properties.find((p) => p.id === deal.propertyId) : null), [deal, store.properties]);

  const [activityKey, setActivityKey] = useState(0);
  const activities = useDealActivity(dealId, { limit: 50, refreshKey: activityKey });

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetEventKey, setSheetEventKey] = useState(null);
  const [composer, setComposer] = useState("");
  const [composerBusy, setComposerBusy] = useState(false);

  /* Compute the latest event label + iso for the hero's
   * "Last updated" line (iter-170 unified momentum). */
  const latestEvent = useMemo(() => {
    const top = (activities || []).find((a) => a?.title);
    if (!top) return null;
    const iso = top.iso || top.createdAt || top.created_at || top.occurredAt || top.occurred_at || top.timestamp || top.ts || null;
    return { title: top.title, iso };
  }, [activities]);
  const latestEventLabel = latestEvent?.title || null;
  const latestEventIso   = latestEvent?.iso   || null;

  /* Build operational workstream rows (table view). */
  const wsRows = useMemo(() => buildWorkstreamRows(deal, activities, store.contacts), [deal, activities, store.contacts]);

  /* Build involved parties for the right rail. */
  const parties = useMemo(() => buildParties(deal, store.contacts), [deal, store.contacts]);

  /* Quick-link URLs derived from deal / property metadata. */
  const quickLinks = useMemo(() => ({
    rightmove:   property?.rightmoveUrl || (property?.address ? `https://www.rightmove.co.uk/property-for-sale/find.html?searchLocation=${encodeURIComponent(property.address)}` : null),
    zoopla:      property?.zooplaUrl   || (property?.address ? `https://www.zoopla.co.uk/for-sale/property/?q=${encodeURIComponent(property.address)}` : null),
    agent:       property?.agentUrl    || (user?.org_slug ? `https://${user.org_slug}.chainflow.app` : null),
    epc:         property?.epcUrl      || "https://www.gov.uk/find-energy-certificate",
    propertyLog: property?.id ? `/properties/${property.id}` : null,
  }), [property, user]);

  const onLogProgress = useCallback(() => {
    setSheetEventKey(null);
    setSheetOpen(true);
  }, []);
  const onUpdateStage = onLogProgress;
  const onSendChase = useCallback(() => toast.info("Send chase — composer opening next."), []);
  const onShare = useCallback(() => {
    if (navigator.share && deal?.name) {
      navigator.share({ title: deal.name, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href).then(
        () => toast.success("Deal link copied"),
        () => toast.error("Couldn't copy link"),
      );
    }
  }, [deal]);
  const onCopyListingLink = useCallback(() => {
    navigator.clipboard?.writeText(quickLinks.rightmove || window.location.href).then(
      () => toast.success("Listing link copied"),
      () => toast.error("Couldn't copy listing link"),
    );
  }, [quickLinks]);
  const onSubmitNote = useCallback(async (text) => {
    if (!deal) return;
    setComposerBusy(true);
    try {
      await store.addActivity?.(deal.id, { text, kind: "note" });
      invalidateDealActivity(deal.id);
      setActivityKey((k) => k + 1);
      setComposer("");
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not log note.");
    } finally {
      setComposerBusy(false);
    }
  }, [deal, store]);

  if (!deal) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar />
        <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen grid place-items-center">
          <div className="text-center">
            <div className="text-sm text-slate-500 mb-2">Loading deal…</div>
            <Link to="/deal-rooms" className="text-violet-700 hover:text-violet-900 text-sm">← Back to Deal Rooms</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main
        className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-w-0 px-4 sm:px-6 lg:px-8 py-5 lg:py-7 pb-32 overflow-x-hidden"
        data-testid="dealroom-main"
      >
        <TopActionBar
          dealName={deal.name}
          onLogProgress={onLogProgress}
          onUpdateStage={onUpdateStage}
          onSendChase={onSendChase}
          onShare={onShare}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
          {/* LEFT — hero + journey + workstreams + activity */}
          <div className="min-w-0 space-y-5">
            <PropertyHero deal={deal} property={property} latestEventLabel={latestEventLabel} latestEventIso={latestEventIso} />
            <TransactionJourney deal={deal} activities={activities} />

            {/* iter-181 · Phase 2 — Since your last visit + Current blockers
             *  Calm operational continuity strip + compact horizontal
             *  blocker rail. Sit directly below the transaction journey
             *  and above the operational workstreams table. */}
            <SinceLastVisitStrip
              deal={{ ...deal, activity: activities }}
              documents={deal?.documents || []}
              intel={deal?.intel || deal}
              lastVisitIso={deal?.last_visit_at || deal?.intel?.last_visit_at}
              onJumpToTimeline={() => {
                const el = document.querySelector('[data-testid="dealroom-activity"]');
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            />
            <CompactBlockerRail
              deal={{ ...deal, activity: activities }}
              documents={deal?.documents || []}
              docRequests={deal?.docRequests || []}
              intel={deal?.intel || deal}
              onChase={(blocker) => onSendChase?.({
                id: `blocker-${blocker.id}`,
                title: `Chase ${blocker.owner}`,
                owner: blocker.owner,
                context: blocker.title,
              })}
              onViewAll={() => {
                const el = document.querySelector('[data-testid="dealroom-activity"]');
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            />

            <OperationalWorkstreamsTable rows={wsRows} onOpenAdd={() => toast.info("Add workstream — coming next.")} />
            <ActivityFeed
              rows={activities}
              value={composer}
              setValue={setComposer}
              onSubmit={onSubmitNote}
              busy={composerBusy}
            />
          </div>

          {/* RIGHT RAIL */}
          <aside className="space-y-4">
            <StatusOverviewCard allClear={true} />
            <KeyDatesCard targetExchange={deal.targetExchange} targetCompletion={deal.targetCompletion} activities={activities} latestEventIso={latestEventIso} />
            <InvolvedPartiesCard parties={parties} onManage={() => navigate(`/deal-rooms/${dealId}/legacy`)} />
            <DealHealthCard tone="emerald" />
            <QuickLinksCard links={quickLinks} onCopy={onCopyListingLink} />
          </aside>
        </div>

        {sheetOpen && (
          <OperationalEventSheet
            open={sheetOpen}
            onClose={() => { setSheetOpen(false); setSheetEventKey(null); }}
            dealId={dealId}
            initialEventKey={sheetEventKey}
            onLogged={() => { invalidateDealActivity(dealId); setActivityKey((k) => k + 1); }}
          />
        )}
      </main>
    </div>
  );
}

/* ────────────────────── Builders ────────────────────── */
function buildWorkstreamRows(deal, activities, contacts) {
  if (!deal) return [];
  const byId = new Map((contacts || []).map((c) => [c.id, c]));
  const ownerOf = (cid) => {
    if (!cid) return { name: "—", role: "" };
    const c = byId.get(cid);
    return { name: c?.name || c?.email || "—", role: c?.role || "" };
  };
  const isLease = (deal.tenure || "").toLowerCase().includes("lease");
  const acts = (activities || []);
  const has = (k) => acts.some((a) => (a?.meta?.event_key || a?.event_key) === k);

  const rows = [
    {
      id: "legal", label: "Legal", subtitle: "Conveyancing", Icon: Scale, toneClass: "bg-violet-50 text-violet-700",
      status: has("exchange_agreed") ? "Completed" : "In progress",
      ownerName: ownerOf(deal.buyerSolicitorId).name || "Buyer solicitor",
      ownerRole: "Buyer's solicitor",
      waitingOn: "Seller's solicitor · enquiries",
      waitingDays: 4,
      waitingKind: "silent",
      nextUp: "Enquiries raised",
    },
    isLease && {
      id: "leasehold", label: "Leasehold", subtitle: "Management pack", Icon: Building2, toneClass: "bg-emerald-50 text-emerald-700",
      status: has("management_pack_received") ? "Completed" : "In progress",
      ownerName: ownerOf(deal.sellerSolicitorId).name || "Seller solicitor",
      ownerRole: "Seller's solicitor",
      waitingOn: "Managing agent · LPE1",
      waitingDays: 8,
      waitingKind: "waiting",
      nextUp: "Management pack",
    },
    {
      id: "mortgage", label: "Mortgage", subtitle: deal.mortgageLender ? `Lender: ${deal.mortgageLender}` : "Mortgage funding", Icon: Home, toneClass: "bg-indigo-50 text-indigo-700",
      status: has("mortgage_offer_received") ? "Completed" : "In progress",
      ownerName: ownerOf(deal.brokerId).name || "Mortgage broker",
      ownerRole: "Mortgage broker",
      waitingOn: "Lender · valuation booked",
      waitingDays: 0,
      waitingKind: "fresh",
      nextUp: "Valuation",
    },
    {
      id: "buyer", label: "Buyer", subtitle: "Buyer side", Icon: User, toneClass: "bg-sky-50 text-sky-700",
      status: has("aml_verified") && has("id_approved") ? "Completed" : "On track",
      ownerName: ownerOf(deal.buyerId).name || "Buyer",
      ownerRole: "Buyer",
      waitingOn: "ID documents",
      waitingDays: 1,
      waitingKind: "waiting",
      nextUp: "ID verification",
    },
    {
      id: "seller", label: "Seller", subtitle: "Seller side", Icon: User, toneClass: "bg-amber-50 text-amber-700",
      status: has("ta6_received") ? "Completed" : "On track",
      ownerName: deal.progressorName || "Progressor",
      ownerRole: "Progressor",
      waitingOn: "TA6 form",
      waitingDays: 3,
      waitingKind: "waiting",
      nextUp: "TA6 form",
    },
    {
      id: "chain", label: "Chain", subtitle: `${deal.chainLinks || 3} links`, Icon: Link2, toneClass: "bg-rose-50 text-rose-700",
      status: "On track",
      ownerName: deal.progressorName || "Progressor",
      ownerRole: "Progressor",
      waitingOn: "1 link update",
      waitingDays: 2,
      waitingKind: "waiting",
      nextUp: "Top of chain",
    },
  ].filter(Boolean);
  return rows;
}

function buildParties(deal, contacts) {
  if (!deal) return [];
  const byId = new Map((contacts || []).map((c) => [c.id, c]));
  // Operational wait state mirrors what the Workstreams table is
  // surfacing — keeps the right rail honest about pressure. Calm by
  // default; specific slots carry a waitDays + waitKind so the
  // unified `waitDuration` helper reads the same signal here as
  // it does in the table above.
  const slots = [
    { id: "buyer_solicitor",  roleLabel: "Buyer's solicitor",  cid: deal.buyerSolicitorId,  statusLabel: "Active" },
    { id: "seller_solicitor", roleLabel: "Seller's solicitor", cid: deal.sellerSolicitorId, statusLabel: "Active", waitDays: 4, waitKind: "silent" },
    { id: "broker",           roleLabel: "Mortgage broker",    cid: deal.brokerId,          statusLabel: "Active", waitDays: 0, waitKind: "fresh" },
    { id: "buyer",            roleLabel: "Buyer",              cid: deal.buyerId,           statusLabel: "Active" },
    { id: "progressor",       roleLabel: "Progressor",         cid: null, name: deal.progressorName || "Rhys Drewell", phone: deal.progressorPhone, email: deal.progressorEmail, statusLabel: "Active" },
    { id: "seller",           roleLabel: "Seller",             cid: deal.sellerId,          statusLabel: "Active" },
    { id: "agent",            roleLabel: "Estate agent",       cid: deal.agentId,           statusLabel: "Active" },
  ];
  return slots
    .map((s) => {
      const base = { id: s.id, roleLabel: s.roleLabel, statusLabel: s.statusLabel, waitDays: s.waitDays, waitKind: s.waitKind };
      if (s.cid) {
        const c = byId.get(s.cid);
        if (!c) return null;
        return { ...base, name: c.name || c.email || "—", phone: c.phone, email: c.email };
      }
      if (s.name) return { ...base, name: s.name, phone: s.phone, email: s.email };
      return null;
    })
    .filter(Boolean);
}
